// 2026-10-28 · Sesión del RESTAURANTE (JWT de 7 días): renovación silenciosa y
// caducidad limpia. Lógica pura (sin store ni axios) para poder probarla; la
// conexión con la app está en restaurantSessionRuntime.js.
//
// Ya existía: al arrancar la app (authJwt.getInitialize) se pide
// `authentication/user/{id}` y se guarda el token nuevo → un F5 renueva.
// Faltaba:
//  - Renovar con la app abierta días sin recargar (TPV 24/7): se comprueba el
//    `exp` al volver a la pestaña / foco / red y cada hora; si quedan menos de
//    RENEW_THRESHOLD_MS se renueva (una sola petición; entre pestañas, una
//    renueva y las demás adoptan el token por el evento `storage`).
//  - 401 del restaurante (no los de la sesión PIN del empleado, que siguen
//    igual): un intento de renovación; si el servidor la rechaza → hubs
//    parados, sesión cerrada y UN aviso «Tu sesión ha caducado…».
//
// La sesión PIN del empleado (X-Employee-Session) NO se toca aquí.

import jwtDecode from "jwt-decode";

export const RENEW_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000; // quedan < 3 días → renovar
export const CHECK_INTERVAL_MS = 60 * 60 * 1000; // comprobación periódica (1 h)
export const FIRST_CHECK_DELAY_MS = 30 * 1000; // al arrancar ya renueva getInitialize
export const RETRY_COOLDOWN_MS = 60 * 1000; // tras un fallo de red
export const LOCK_TTL_MS = 30 * 1000; // otra pestaña está renovando
// Un 401 justo después de renovar con éxito no es de caducidad (p. ej. un
// endpoint que contesta 401 sin cuerpo por otra razón): no se renueva otra vez.
export const RECOVER_COOLDOWN_MS = 60 * 1000;
export const HUB_PROBE_COOLDOWN_MS = 5 * 60 * 1000;
export const HUB_PROBE_MIN_FAILURES = 2;

export const RENEW_LOCK_KEY = "tmo:restaurantTokenRenewal";
export const TOKEN_KEY = "accessToken";
export const EXPIRED_NOTICE_KEY = "tmo:restaurantSessionNotice";

export const DEVICE_SESSION_CLOSED_CODE = "DEVICE_SESSION_CLOSED";
export const SESSION_EXPIRED_MESSAGE = "Tu sesión ha caducado. Vuelve a iniciar sesión.";
export const DEVICE_SESSION_CLOSED_MESSAGE =
  "La sesión de este dispositivo se cerró desde otro dispositivo (o por el límite de sesiones). Vuelve a iniciar sesión.";

export function expiredMessageFor(code) {
  return code === DEVICE_SESSION_CLOSED_CODE ? DEVICE_SESSION_CLOSED_MESSAGE : SESSION_EXPIRED_MESSAGE;
}

const isUsableToken = (t) => typeof t === "string" && t !== "" && t !== "null" && t !== "undefined";

/** `exp` del JWT en ms (null si no se puede leer). */
export function tokenExpiresAtMs(token) {
  if (!isUsableToken(token)) return null;
  try {
    const { exp } = jwtDecode(token) || {};
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function headerValue(headers, name) {
  if (!headers) return null;
  try {
    if (typeof headers.get === "function") {
      const v = headers.get(name);
      if (v) return v;
    }
  } catch { /* AxiosHeaders raro */ }
  return headers[name] || headers[name.toLowerCase()] || null;
}

/** JWT que viajó en la petición (Authorization: Bearer …). */
export function sentBearerToken(config) {
  const h = headerValue(config?.headers, "Authorization");
  if (typeof h !== "string") return null;
  const m = h.match(/^Bearer\s+(.+)$/i);
  return m && isUsableToken(m[1].trim()) ? m[1].trim() : null;
}

/** `authentication/user/{id}` (o `authentication`): la propia renovación. */
export function isRenewalUrl(url) {
  const u = String(url || "").replace(/^\/+/, "").split("?")[0].toLowerCase();
  return u === "authentication" || u.startsWith("authentication/user/");
}

/**
 * ¿Es un 401 del token del RESTAURANTE? Solo:
 *  - el reto del JWT (cuerpo vacío) de una petición que llevó Bearer, o
 *  - DEVICE_SESSION_CLOSED (el API no renueva un dispositivo cerrado).
 * Cualquier 401 con código o mensaje (sesión PIN del empleado, contraseña o
 * PIN incorrectos…) NO lo es: sigue su flujo de siempre.
 */
export function isRestaurantAuthFailure(error) {
  const cfg = error?.config;
  if (!cfg || cfg.__isRestaurantRenewal) return false;
  if ((error?.response?.status ?? error?.status) !== 401) return false;
  const body = error?.response?.data;
  if (body && typeof body === "object" && body.code === DEVICE_SESSION_CLOSED_CODE) return true;
  if (body && typeof body === "object" && Object.keys(body).length > 0) return false;
  if (typeof body === "string" && body.trim() !== "") return false;
  return !!sentBearerToken(cfg);
}

/** Copia de la petición con otro token, marcada para no reintentar más. */
export function retryConfigWithToken(config, token) {
  const headers = { ...(config?.headers || {}) };
  delete headers.authorization;
  headers.Authorization = `Bearer ${token}`;
  return { ...config, headers, __restaurantAuthRetried: true, skipDedupe: true };
}

/**
 * Programador de renovación + recuperación ante 401.
 *
 * deps:
 *  - storage: localStorage (token y cerrojo entre pestañas)
 *  - isActive(): hay sesión de restaurante (no comensal QR, no anónimo)
 *  - renew(): renueva (Promise); rechaza con el error HTTP si falla
 *  - adoptToken(token): otra pestaña renovó → usar su token (sin red)
 *  - onExpired(code): el servidor rechazó la renovación (401)
 */
export function createRestaurantSessionRenewal({
  storage,
  isActive,
  renew,
  adoptToken = () => {},
  onExpired = () => {},
  now = () => Date.now(),
  tabId = `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`,
  thresholdMs = RENEW_THRESHOLD_MS,
} = {}) {
  let inflight = null;
  let lastFailureAt = null;
  let lastProbeAt = null;
  let lastRenewedAt = null;

  const readToken = () => {
    try {
      const t = storage?.getItem(TOKEN_KEY);
      return isUsableToken(t) ? t : null;
    } catch {
      return null;
    }
  };

  const readLock = () => {
    try {
      const raw = storage?.getItem(RENEW_LOCK_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const otherTabRenewing = () => {
    const lock = readLock();
    return !!lock && lock.owner !== tabId && now() - Number(lock.at || 0) < LOCK_TTL_MS;
  };

  const takeLock = () => {
    try { storage?.setItem(RENEW_LOCK_KEY, JSON.stringify({ owner: tabId, at: now() })); } catch { /* ignore */ }
  };

  const releaseLock = () => {
    try {
      if (readLock()?.owner === tabId) storage?.removeItem(RENEW_LOCK_KEY);
    } catch { /* ignore */ }
  };

  const remainingMs = () => {
    const exp = tokenExpiresAtMs(readToken());
    return exp == null ? null : exp - now();
  };

  /** Una sola renovación en vuelo por pestaña. Resultado: renewed | expired | failed | skipped. */
  function renewNow({ force = false } = {}) {
    if (inflight) return inflight;
    if (!force && otherTabRenewing()) return Promise.resolve("skipped");
    takeLock();
    inflight = Promise.resolve()
      .then(() => renew())
      .then(() => {
        lastFailureAt = null;
        lastRenewedAt = now();
        return "renewed";
      })
      .catch((err) => {
        const status = err?.status ?? err?.response?.status;
        if (status === 401) {
          const code = err?.response?.data?.code || err?.data?.code || null;
          try { onExpired(code); } catch { /* ignore */ }
          return "expired";
        }
        lastFailureAt = now();
        return "failed";
      })
      .finally(() => {
        releaseLock();
        inflight = null;
      });
    return inflight;
  }

  return {
    readToken,
    remainingMs,
    /** Renueva solo si quedan < umbral (y no hay otra pestaña en ello). */
    check() {
      if (!isActive()) return Promise.resolve("inactive");
      const left = remainingMs();
      if (left == null) return Promise.resolve("no-token");
      if (left > thresholdMs) return Promise.resolve("fresh");
      if (lastFailureAt != null && now() - lastFailureAt < RETRY_COOLDOWN_MS) return Promise.resolve("cooldown");
      return renewNow();
    },
    /** Tras un 401 del restaurante: un intento de renovación (ignora el umbral). */
    recover() {
      if (!isActive()) return Promise.resolve("inactive");
      if (inflight) return inflight;
      if (lastRenewedAt != null && now() - lastRenewedAt < RECOVER_COOLDOWN_MS) return Promise.resolve("recent");
      return renewNow({ force: true });
    },
    /**
     * Un hub no conecta: con SignalR por WebSocket el 401 no se ve, así que
     * tras varios fallos (o con el token ya vencido) se pregunta al API por
     * REST, como mucho cada HUB_PROBE_COOLDOWN_MS. 401 → caducada.
     */
    onHubStartFailure({ failures = 0 } = {}) {
      if (!isActive()) return Promise.resolve("inactive");
      const left = remainingMs();
      const expired = left != null && left <= 0;
      if (!expired && failures < HUB_PROBE_MIN_FAILURES) return Promise.resolve("wait");
      if (lastProbeAt != null && now() - lastProbeAt < HUB_PROBE_COOLDOWN_MS) return Promise.resolve("cooldown");
      lastProbeAt = now();
      return renewNow({ force: true });
    },
    /** Evento `storage`: otra pestaña guardó un token nuevo → adoptarlo. */
    onStorage(event) {
      if (!event || event.key !== TOKEN_KEY) return false;
      if (!isUsableToken(event.newValue) || !isActive()) return false;
      try { adoptToken(event.newValue); } catch { /* ignore */ }
      return true;
    },
    isRenewing: () => !!inflight,
    /** Nueva sesión (o tests): olvida esperas y renovaciones previas. */
    reset() {
      lastFailureAt = null;
      lastProbeAt = null;
      lastRenewedAt = null;
    },
  };
}

/** Aviso único: varias peticiones en vuelo con 401 → una sola caducidad. */
export function createExpiryOnce({ now = () => Date.now(), windowMs = 15000, run }) {
  let lastAt = -Infinity;
  const fire = (code) => {
    if (now() - lastAt < windowMs) return false;
    lastAt = now();
    run(code);
    return true;
  };
  fire.reset = () => { lastAt = -Infinity; };
  return fire;
}

/** Deja el aviso para la pantalla de login (sobrevive al `localStorage.clear()` del login). */
export function rememberExpiredNotice(code, storage = typeof window !== "undefined" ? window.sessionStorage : null) {
  try { storage?.setItem(EXPIRED_NOTICE_KEY, expiredMessageFor(code)); } catch { /* ignore */ }
}

/** Aviso pendiente para el login (null si no hay). */
export function readExpiredNotice(storage = typeof window !== "undefined" ? window.sessionStorage : null) {
  try {
    return storage?.getItem(EXPIRED_NOTICE_KEY) || null;
  } catch {
    return null;
  }
}

export function clearExpiredNotice(storage = typeof window !== "undefined" ? window.sessionStorage : null) {
  try { storage?.removeItem(EXPIRED_NOTICE_KEY); } catch { /* ignore */ }
}
